
import { action, observable } from 'mobx';
import * as Models from 'Models/Entities';
import { atbStatusOptions } from 'Models/Enums/MappedEnumOptions';
import {
	attribute,
	entity,
	IModelAttributes,
	Model,
	ReferencePath,
} from 'Models/Model';
import UserAtbFileEntity from 'Models/Security/Acl/UserAtbFileEntity';
import VisitorsAtbFileEntity from 'Models/Security/Acl/VisitorsAtbFileEntity';
import { IAcl } from 'Models/Security/IAcl';
import moment from 'moment';
import * as AttrUtils from 'Util/AttributeUtils';
import {
	getCreatedModifiedCrudOptions,
	makeEnumFetchFunction,
	makeFetchOneToManyFunc,
} from 'Util/EntityUtils';
import * as Validators from 'Validators';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { CRUD } from '../CRUDOptions';
import * as Enums from '../Enums';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IAtbFileEntityAttributes extends IModelAttributes {
	atbFileType: Enums.atbFileType;
	status: Enums.atbStatus;
	targetMonth: Date;
	fileName: string;
	addNewCustomers: boolean;

	businessEntityId: string;
	businessEntity: Models.BusinessEntity | Models.IBusinessEntityAttributes;
	userId: string;
	user: Models.UserEntity | Models.IUserEntityAttributes;
	atbRecords: Array<
		| Models.AtbRecordEntity
		| Models.IAtbRecordEntityAttributes
	>;

}

@entity('AtbFileEntity', 'ATB File')
export default class AtbFileEntity extends Model
	implements IAtbFileEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserAtbFileEntity(),
		new VisitorsAtbFileEntity(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<AtbFileEntity, Enums.atbStatus>()
	@CRUD({
		name: 'ATB Status',
		displayType: 'enum-combobox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => (AttrUtils.standardiseEnum(attr, atbStatusOptions)),
		enumResolveFunction: makeEnumFetchFunction(atbStatusOptions),
		displayFunction: (attr: Enums.atbStatus) => atbStatusOptions[attr],
	})
	public status: Enums.atbStatus;

	@observable
	@attribute<AtbFileEntity, Enums.atbFileType>()
	@CRUD({
		name: 'ATB File Type',
		displayType: 'enum-combobox',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.atbFileTypeOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.atbFileTypeOptions),
		displayFunction: (attr: Enums.atbFileType) => Enums.atbFileTypeOptions[attr],
	})
	public atbFileType: Enums.atbFileType;

	/**
	 * Target Year and Month for the data. Day should always be 0.
	 */
	@observable
	@attribute<AtbFileEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	@CRUD({
		name: 'Target Month',
		displayType: 'datepicker',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: attr => AttrUtils.standardiseDate(attr, { serialiseAs: 'date' }),
	})
	public targetMonth: Date;

	@observable
	@attribute<AtbFileEntity, string>()
	@CRUD({
		name: 'File name',
		displayType: 'textfield',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public fileName: string;

	/**
	 * Whether or not new customers should be added by default
	 */
	@observable
	@attribute<AtbFileEntity, boolean>()
	@CRUD({
		name: 'Add New Customers',
		displayType: 'checkbox',
		order: 60,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public addNewCustomers: boolean;


	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		name: 'Business Entity',
		displayType: 'reference-combobox',
		order: 80,
		referenceTypeFunc: () => Models.BusinessEntity,
	})
	public businessEntityId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public businessEntity: Models.BusinessEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		name: 'User',
		displayType: 'reference-combobox',
		order: 90,
		referenceTypeFunc: () => Models.UserEntity,
	})
	public userId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user: Models.UserEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		name: 'ATB Records',
		displayType: 'reference-multicombobox',
		order: 100,
		referenceTypeFunc: () => Models.AtbRecordEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'atbRecords',
			oppositeEntity: () => Models.AtbRecordEntity,
		}),
	})
	public atbRecords: Models.AtbRecordEntity[] = [];

	@observable
	public countAtbRecordErrors: number;

	@observable
	public countAtbRecordWarnings: number;

	@observable
	public processedAtbRecords: number = 0;

	@observable
	public totalAtbRecords: number = 0;

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IAtbFileEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IAtbFileEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.status !== undefined) {
				this.status = attributes.status;
			}
			if (attributes.atbFileType !== undefined) {
				this.atbFileType = attributes.atbFileType;
			}
	
			if (attributes.targetMonth !== undefined) {
				if (attributes.targetMonth === null) {
					this.targetMonth = attributes.targetMonth;
				} else {
					this.targetMonth = moment(attributes.targetMonth).toDate();
				}
			}
			if (attributes.fileName !== undefined) {
				this.fileName = attributes.fileName;
			}
			if (attributes.addNewCustomers !== undefined) {
				this.addNewCustomers = attributes.addNewCustomers;
			}
		
			if (attributes.businessEntityId !== undefined) {
				this.businessEntityId = attributes.businessEntityId;
			}
			if (attributes.businessEntity !== undefined) {
				if (attributes.businessEntity === null) {
					this.businessEntity = attributes.businessEntity;
				} else if (attributes.businessEntity instanceof Models.BusinessEntity) {
					this.businessEntity = attributes.businessEntity;
					this.businessEntityId = attributes.businessEntity.id;
				} else {
					this.businessEntity = new Models.BusinessEntity(attributes.businessEntity);
					this.businessEntityId = this.businessEntity.id;
				}
			}
			if (attributes.userId !== undefined) {
				this.userId = attributes.userId;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
			if (attributes.atbRecords !== undefined && Array.isArray(attributes.atbRecords)) {
				for (const model of attributes.atbRecords) {
					if (model instanceof Models.AtbRecordEntity) {
						this.atbRecords.push(model);
					} else {
						this.atbRecords.push(new Models.AtbRecordEntity(model));
					}
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		businessEntity {
			${Models.BusinessEntity.getAttributes().join('\n')}
		}
		user {
			${Models.UserEntity.getAttributes().join('\n')}
		}
		atbRecords {
			${Models.AtbRecordEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			atbRecords: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(AtbFileEntity.prototype, 'created');
CRUD(modifiedAttr)(AtbFileEntity.prototype, 'modified');
